<template>
  <div class="materijal-edit">
    <header class="materijal-edit__header">
      <div class="materijal-edit__header-top">
        <div class="materijal-edit__header-left">
          <nav class="materijal-edit__breadcrumb">
            <router-link :to="{ name: 'Materijali' }" class="materijal-edit__breadcrumb-back" aria-label="Back to Materials">
              <span class="material-symbols-outlined">arrow_back</span>
            </router-link>
            <router-link :to="{ name: 'Materijali' }" class="materijal-edit__breadcrumb-link">Materials</router-link>
            <span class="materijal-edit__breadcrumb-sep">/</span>
            <span class="materijal-edit__breadcrumb-current">Edit Item</span>
          </nav>
          <h2 class="materijal-edit__title">
            <span class="materijal-edit__title-main">{{ titleMain }}</span>
            <span v-if="titleSuffix" class="materijal-edit__title-suffix"> {{ titleSuffix }}</span>
          </h2>
          <div class="materijal-edit__meta-row">
            <span class="materijal-edit__meta">ID: {{ displayId }}</span>
            <span class="materijal-edit__meta">Last edited {{ form.lastEdited || '—' }}</span>
          </div>
        </div>
        <div class="materijal-edit__header-right">
          <div class="materijal-edit__lang-toggles">
            <button
              v-for="(lang, key) in langs"
              :key="key"
              type="button"
              class="materijal-edit__lang-btn"
              :class="{ 'materijal-edit__lang-btn--active': selectedLang === key }"
              @click="selectedLang = key"
            >
              {{ lang }}
            </button>
          </div>
          <a
            :href="previewPageUrl"
            target="_blank"
            rel="noopener"
            class="el-button materijal-edit__btn-preview"
            @click="onPreviewClick"
          >
            <span class="material-symbols-outlined">visibility</span>
            Preview
          </a>
          <el-button type="button" class="el-button materijal-edit__btn-save" @click="publishChanges">
            <span class="material-symbols-outlined">save</span>
            Save
          </el-button>
        </div>
      </div>
    </header>

    <div class="materijal-edit__body">
      <div class="materijal-edit__main">
        <div
          v-for="(sectionId, sectionIndex) in sectionOrder"
          :key="sectionId"
          class="materijal-edit__section-wrap"
          :class="{ 'materijal-edit__section-wrap--dragging': sectionDraggedIndex === sectionIndex }"
          draggable="true"
          @dragstart="onSectionDragStart($event, sectionIndex)"
          @dragend="onSectionDragEnd"
          @dragover.prevent="onSectionDragOver($event, sectionIndex)"
          @drop="onSectionDrop($event, sectionIndex)"
        >
          <span class="materijal-edit__section-drag-handle" aria-hidden="true">
            <span class="material-symbols-outlined">drag_indicator</span>
          </span>
          <div v-if="sectionId === 'general'" class="materijal-edit__card">
            <h3 class="materijal-edit__card-title">
              <span class="material-symbols-outlined materijal-edit__card-icon">format_list_bulleted</span>
              General information
            </h3>
            <div class="materijal-edit__form-row">
              <div class="materijal-edit__form-field materijal-edit__form-field--equal">
                <label class="materijal-edit__form-label">TITLE</label>
                <el-input
                  v-model="form.data[selectedLang].displayName"
                  placeholder="Enter title"
                  class="materijal-edit__form-input"
                />
              </div>
              <div class="materijal-edit__form-field materijal-edit__form-field--equal">
                <label class="materijal-edit__form-label">URL SLUG</label>
                <div class="materijal-edit__url-wrap">
                  <span class="materijal-edit__url-prefix">{{ urlPrefix }}/</span>
                  <el-input
                    v-model="form.data[selectedLang].urlSlug"
                    placeholder="material-slug"
                    class="materijal-edit__form-input materijal-edit__form-input--slug"
                  />
                </div>
              </div>
            </div>
            <div class="materijal-edit__form-group">
              <label class="materijal-edit__form-label">DESCRIPTION</label>
              <textarea
                v-model="form.data[selectedLang].narrative"
                class="materijal-edit__form-textarea"
                rows="4"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div v-else-if="sectionId === 'galerija'" class="materijal-edit__card">
            <h3 class="materijal-edit__card-title">
              <span class="material-symbols-outlined materijal-edit__card-icon">grid_view</span>
              Galerija
            </h3>
            <input
              ref="galleryFileInput"
              type="file"
              accept="image/*"
              multiple
              class="materijal-edit__file-input"
              @change="onGalleryFilesChange"
            >
            <div class="materijal-edit__gallery-grid">
              <div
                v-for="(img, index) in form.gallery"
                :key="`gallery-${index}`"
                class="materijal-edit__gallery-slot"
                :class="{ 'materijal-edit__gallery-slot--dragging': galleryDraggedIndex === index }"
                draggable="true"
                @dragstart="onGalleryDragStart($event, index)"
                @dragend="onGalleryDragEnd"
                @dragover.prevent="onGalleryDragOver($event, index)"
                @drop="onGalleryDropItem($event, index)"
              >
                <img :src="img.url" class="materijal-edit__gallery-slot-img" alt="">
                <button
                  type="button"
                  class="materijal-edit__gallery-slot-remove"
                  aria-label="Remove"
                  @click="removeGalleryImage(index)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
                <span class="materijal-edit__gallery-drag-handle" aria-hidden="true">
                  <span class="material-symbols-outlined">drag_indicator</span>
                </span>
              </div>
              <button
                type="button"
                class="materijal-edit__gallery-slot materijal-edit__gallery-slot--add"
                @click="openGalleryFileInput"
              >
                <span class="material-symbols-outlined">add</span>
              </button>
            </div>
            <div
              class="materijal-edit__dropzone"
              :class="{ 'materijal-edit__dropzone--active': isDropzoneActive }"
              @dragover.prevent="isDropzoneActive = true"
              @dragleave="isDropzoneActive = false"
              @drop.prevent="onGalleryDrop"
              @click="openGalleryFileInput"
            >
              <span class="material-symbols-outlined materijal-edit__dropzone-icon">cloud_upload</span>
              <p class="materijal-edit__dropzone-text">Drag and drop high-res imagery</p>
              <p class="materijal-edit__dropzone-hint">SUPPORT: TIFF, JPEG, WEBP - UP TO 40MB</p>
              <button type="button" class="materijal-edit__dropzone-btn" @click.stop="openGalleryFileInput">BROWSE LOCAL FILES</button>
            </div>
          </div>

          <div v-else-if="sectionId === 'youtube'" class="materijal-edit__card">
            <h3 class="materijal-edit__card-title">
              <span class="material-symbols-outlined materijal-edit__card-icon">videocam</span>
              YouTube video
            </h3>
            <div class="materijal-edit__form-group">
              <label class="materijal-edit__form-label">EMBED LINK (YOUTUBE / VIMEO)</label>
              <div class="materijal-edit__input-with-icon">
                <span class="material-symbols-outlined materijal-edit__input-icon">link</span>
                <el-input
                  v-model="form.videoEmbedUrl"
                  placeholder="https://youtube.com/watch?v=..."
                  class="materijal-edit__form-input"
                />
              </div>
            </div>
            <div v-if="videoEmbedSrc" class="materijal-edit__video-wrap">
              <iframe
                :src="videoEmbedSrc"
                class="materijal-edit__video-iframe"
                frameborder="0"
                allowfullscreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
            <div v-else class="materijal-edit__video-placeholder">
              <span class="material-symbols-outlined materijal-edit__video-placeholder-icon">play_circle</span>
              <span class="materijal-edit__video-placeholder-text">PREVIEW VIDEO</span>
            </div>
          </div>
        </div>
      </div>

      <aside class="materijal-edit__sidebar">
        <div class="materijal-edit__card">
          <h3 class="materijal-edit__card-title">
            <span class="material-symbols-outlined materijal-edit__card-icon">description</span>
            Primary Covers
          </h3>
          <div class="materijal-edit__covers">
            <div class="materijal-edit__cover-item">
              <span class="materijal-edit__cover-label">TILE IMAGE (4:3)</span>
              <input
                ref="squareTileInput"
                type="file"
                accept="image/*"
                class="materijal-edit__file-input"
                @change="onSquareTileChange"
              >
              <div
                class="materijal-edit__cover-box materijal-edit__cover-box--square"
                @click="$refs.squareTileInput.click()"
              >
                <img v-if="form.squareTile" :src="form.squareTile" class="materijal-edit__cover-img" alt="">
                <span v-else class="materijal-edit__cover-placeholder">
                  <span class="material-symbols-outlined">add_photo_alternate</span>
                </span>
              </div>
            </div>
            <div class="materijal-edit__cover-item">
              <span class="materijal-edit__cover-label">HERO BANNER (21:9)</span>
              <input
                ref="heroBannerInput"
                type="file"
                accept="image/*"
                class="materijal-edit__file-input"
                @change="onHeroBannerChange"
              >
              <div
                class="materijal-edit__cover-box materijal-edit__cover-box--hero"
                @click="$refs.heroBannerInput.click()"
              >
                <img v-if="form.heroBanner" :src="form.heroBanner" class="materijal-edit__cover-img" alt="">
                <span v-else class="materijal-edit__cover-placeholder">
                  <span class="material-symbols-outlined">add_photo_alternate</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="materijal-edit__card">
          <h3 class="materijal-edit__card-title">
            <span class="material-symbols-outlined materijal-edit__card-icon">category</span>
            Material Category
          </h3>
          <div class="materijal-edit__form-group">
            <el-select
              :value="materialCategorySelectValue"
              placeholder="Select category"
              class="materijal-edit__form-select"
              @change="onMaterialCategorySelect"
            >
              <el-option :value="ADD_NEW_CATEGORY_VALUE" label="Add new category" />
              <el-option
                v-for="cat in materialCategorySelectOptions"
                :key="cat"
                :value="cat"
                :label="cat"
              />
            </el-select>
            <div v-if="isAddingNewCategory" class="materijal-edit__add-row materijal-edit__add-row--mt">
              <el-input
                ref="newCategoryInput"
                :value="pendingNewCategory"
                placeholder="Enter new category name"
                class="materijal-edit__add-input"
                @input="pendingNewCategory = $event"
                @blur="commitNewCategory"
              />
            </div>
          </div>
        </div>

        <div class="materijal-edit__card">
          <h3 class="materijal-edit__card-title">
            <span class="material-symbols-outlined materijal-edit__card-icon">label</span>
            Label
          </h3>
          <div class="materijal-edit__form-group">
            <label class="materijal-edit__form-label">TAGS</label>
            <div class="materijal-edit__label-row">
              <el-input
                :value="tagValue"
                placeholder="Enter tag"
                class="materijal-edit__form-input materijal-edit__form-input--tag"
                @input="onTagInput"
              />
              <div class="materijal-edit__color-picker-wrap" ref="colorPickerWrap">
                <input
                  ref="colorInput"
                  type="color"
                  :value="displayColorHex"
                  class="materijal-edit__color-input"
                  @input="onColorInput"
                >
                <button
                  type="button"
                  class="materijal-edit__color-picker-btn"
                  :style="{ backgroundColor: displayColorHex }"
                  aria-label="Pick color"
                  aria-haspopup="true"
                  :aria-expanded="colorPickerOpen"
                  @click="toggleColorPicker"
                />
                <div
                  v-show="colorPickerOpen"
                  class="materijal-edit__color-picker-dropdown"
                  ref="colorPickerDropdown"
                >
                  <div class="materijal-edit__color-swatch-row materijal-edit__color-swatch-row--primary">
                    <button
                      v-for="c in colorSwatchesPrimary"
                      :key="c"
                      type="button"
                      class="materijal-edit__color-swatch"
                      :style="{ backgroundColor: c }"
                      :title="c"
                      @click="selectColor(c); colorPickerOpen = false; removeColorPickerListener()"
                    />
                  </div>
                  <div class="materijal-edit__color-swatch-row materijal-edit__color-swatch-row--neutrals">
                    <button
                      v-for="(c, idx) in colorSwatchesNeutrals"
                      :key="`neutral-${idx}-${c.hex}-${c.none}`"
                      type="button"
                      class="materijal-edit__color-swatch"
                      :class="{ 'materijal-edit__color-swatch--none': c.none }"
                      :style="c.none ? {} : { backgroundColor: c.hex }"
                      :title="c.none ? 'No color' : c.hex"
                      @click="selectColor(c.hex); colorPickerOpen = false; removeColorPickerListener()"
                    />
                  </div>
                  <div class="materijal-edit__color-swatch-row materijal-edit__color-swatch-row--spectrum">
                    <button
                      v-for="c in colorSwatchesSpectrum"
                      :key="c"
                      type="button"
                      class="materijal-edit__color-swatch"
                      :style="{ backgroundColor: c }"
                      :title="c"
                      @click="selectColor(c); colorPickerOpen = false; removeColorPickerListener()"
                    />
                  </div>
                  <button
                    type="button"
                    class="materijal-edit__color-show-more"
                    @click="openNativeColorPicker(); colorPickerOpen = false; removeColorPickerListener()"
                  >
                    Show Colors...
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="materijal-edit__card">
          <h3 class="materijal-edit__card-title materijal-edit__card-title--uppercase">VISIBILITY</h3>
          <div class="materijal-edit__active-toggle">
            <el-switch
              v-model="form.active"
              active-text="Active"
              inactive-text="Inactive"
            />
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Message, Input, Button, Switch, Select, Option } from 'element-ui'

const ADD_NEW_CATEGORY_VALUE = '__add_new__'
const MATERIAL_CATEGORY_OPTIONS = []

function parseVideoEmbedUrl (url) {
  if (!url || typeof url !== 'string') return ''
  const trimmed = url.trim()
  let m = trimmed.match(/youtube\.com\/watch\?v=([^&]+)/)
  if (m) return `https://www.youtube.com/embed/${m[1]}`
  m = trimmed.match(/youtu\.be\/([^?]+)/)
  if (m) return `https://www.youtube.com/embed/${m[1]}`
  m = trimmed.match(/vimeo\.com\/(\d+)/)
  if (m) return `https://player.vimeo.com/video/${m[1]}`
  return ''
}

const COLOR_SWATCHES_PRIMARY = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#ec4899', '#92400e', '#a16207', '#94a3b8', '#ffffff']
const COLOR_SWATCHES_NEUTRALS = [
  { hex: '#000000', none: true },
  { hex: '#ffffff', none: false },
  { hex: '#f1f5f9', none: false },
  { hex: '#e2e8f0', none: false },
  { hex: '#cbd5e1', none: false },
  { hex: '#94a3b8', none: false },
  { hex: '#64748b', none: false },
  { hex: '#475569', none: false },
  { hex: '#334155', none: false },
  { hex: '#1e293b', none: false },
  { hex: '#000000', none: false }
]
const COLOR_SWATCHES_SPECTRUM = [
  '#fef2f2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d', '#450a0a', '#2d0707',
  '#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#f97316', '#ea580c', '#c2410c', '#9a3412', '#7c2d12', '#431407', '#2c0d05',
  '#fefce8', '#fef9c3', '#fef08a', '#fde047', '#eab308', '#ca8a04', '#a16207', '#854d0e', '#713f12', '#422006', '#291a04',
  '#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d', '#052e16', '#031f0e',
  '#ecfeff', '#cffafe', '#a5f3fc', '#67e8f9', '#06b6d4', '#0891b2', '#0e7490', '#155e75', '#164e63', '#083344', '#051a22',
  '#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a', '#172554', '#0f1729',
  '#eef2ff', '#e0e7ff', '#c7d2fe', '#a5b4fc', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81', '#1e1b4b', '#13102e',
  '#fdf2f8', '#fce7f3', '#fbcfe8', '#f9a8d4', '#ec4899', '#db2777', '#be185d', '#9d174d', '#831843', '#500724', '#330518',
  '#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#a855f7', '#9333ea', '#7e22ce', '#6b21a8', '#581c87', '#3b0764', '#25063f',
  '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f', '#451a03', '#2d1208'
]

const NAMED_COLOR_TO_HEX = {
  gray: '#6b7280',
  orange: '#f97316',
  'blue-gray': '#64748b',
  dark: '#1e293b',
  purple: '#9333ea'
}

function namedColorToHex (name) {
  if (!name || /^#[0-9a-fA-F]{6}$/.test(name)) return name || '#000000'
  return NAMED_COLOR_TO_HEX[name] || '#000000'
}

function getEmptyLangData () {
  return { displayName: '', urlSlug: '', narrative: '' }
}

function getEmptyForm () {
  return {
    id: null,
    data: {
      rs: getEmptyLangData(),
      en: getEmptyLangData()
    },
    gallery: [],
    videoEmbedUrl: '',
    squareTile: '',
    heroBanner: '',
    materialCategories: [],
    tags: [],
    tagColor: '#000000',
    active: true,
    created: '',
    lastUpdate: '',
    lastEdited: '',
    editor: ''
  }
}

export default {
  name: 'MaterijalEdit',

  components: {
    ElInput: Input,
    ElButton: Button,
    ElSwitch: Switch,
    ElSelect: Select,
    ElOption: Option
  },

  data () {
    return {
      selectedLang: 'rs',
      langs: { rs: 'Srpski', en: 'Engleski' },
      form: getEmptyForm(),
      sectionOrder: ['general', 'galerija', 'youtube'],
      sectionDraggedIndex: null,
      galleryDraggedIndex: null,
      isDropzoneActive: false,
      colorPickerOpen: false,
      colorSwatchesPrimary: COLOR_SWATCHES_PRIMARY,
      colorSwatchesNeutrals: COLOR_SWATCHES_NEUTRALS,
      colorSwatchesSpectrum: COLOR_SWATCHES_SPECTRUM,
      isAddingNewCategory: false,
      pendingNewCategory: '',
      ADD_NEW_CATEGORY_VALUE
    }
  },

  computed: {
    ...mapGetters('materijali', ['materialById', 'materials']),
    isNew () {
      return this.$route.query.new === '1'
    },
    materialId () {
      return this.$route.params.materialId
    },
    videoEmbedSrc () {
      return parseVideoEmbedUrl(this.form.videoEmbedUrl)
    },
    materialCategoryValue () {
      const cats = this.form.materialCategories
      return Array.isArray(cats) && cats.length ? cats[0] : ''
    },
    materialCategorySelectValue () {
      if (this.isAddingNewCategory) return ADD_NEW_CATEGORY_VALUE
      return this.materialCategoryValue || ''
    },
    materialCategorySelectOptions () {
      const fromData = (this.materials || []).reduce((acc, m) => {
        const cats = Array.isArray(m.materialCategories) ? m.materialCategories : []
        cats.forEach((c) => { if (c && !acc.includes(c)) acc.push(c) })
        return acc
      }, [])
      const combined = [...MATERIAL_CATEGORY_OPTIONS]
      fromData.forEach((c) => { if (c && !combined.includes(c)) combined.push(c) })
      const current = this.materialCategoryValue
      if (current && !combined.includes(current)) combined.push(current)
      return combined
    },
    tagValue () {
      const tags = this.form.tags
      return Array.isArray(tags) && tags.length ? tags[0] : ''
    },
    displayColorHex () {
      return namedColorToHex(this.form.tagColor)
    },
    urlPrefix () {
      const base = process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_FRONTEND
        ? process.env.VUE_APP_LOCAL_FRONTEND
        : 'https://mondial90.rs'
      const path = this.selectedLang === 'en' ? 'en/materijali' : 'materijali'
      return `${base}/${path}`
    },
    displayId () {
      if (!this.form.id) return '—'
      const year = new Date().getFullYear()
      const padded = String(this.form.id).padStart(2, '0')
      return `MAR-${padded}-${year}-001`
    },
    titleMain () {
      const name = this.form.data[this.selectedLang].displayName || 'Untitled Material'
      const parts = name.trim().split(/\s+/)
      if (parts.length <= 1) return name
      return parts.slice(0, -1).join(' ')
    },
    titleSuffix () {
      const name = this.form.data[this.selectedLang].displayName || ''
      const parts = name.trim().split(/\s+/)
      return parts.length > 1 ? parts[parts.length - 1] : ''
    },
    previewPageUrl () {
      const base = process.env.VUE_APP_LOCAL_FRONTEND || ''
      const slug = (this.form.data[this.selectedLang].urlSlug || '').trim()
      if (!base || !slug) return '#'
      const baseClean = base.replace(/\/$/, '')
      const path = this.selectedLang === 'en' ? 'en/materijali' : 'materijali'
      return `${baseClean}/${path}/${encodeURIComponent(slug)}`
    }
  },

  beforeDestroy () {
    this.removeColorPickerListener()
  },

  watch: {
    materialId: {
      immediate: true,
      handler () {
        this.loadMaterial()
      }
    }
  },

  methods: {
    ...mapActions('materijali', ['createMaterial', 'updateMaterial', 'fetchMaterial']),

    loadMaterial () {
      if (this.isNew && this.materialId === 'new') {
        const empty = getEmptyForm()
        empty.data.rs.displayName = this.$route.query.name || ''
        this.form = { ...empty, id: null }
        this.isAddingNewCategory = false
        return
      }
      if (this.isNew) {
        const empty = getEmptyForm()
        empty.data.rs.displayName = this.$route.query.name || ''
        this.form = { ...empty, id: this.materialId }
        this.isAddingNewCategory = false
        return
      }
      const material = this.materialById(this.materialId)
      if (material) {
        const categories = Array.isArray(material.materialCategories) ? material.materialCategories : []
        const tagsStr = material.tags != null ? String(material.tags) : ''
        const tags = tagsStr ? [tagsStr] : []
        const tagColor = material.tagColor || material.tagClass || ''
        const tagColorHex = namedColorToHex(tagColor)
        this.form = {
          id: material.id,
          data: {
            rs: {
              displayName: material.name || material.displayName || '',
              urlSlug: material.urlSlug || '',
              narrative: material.narrative || ''
            },
            en: {
              displayName: material.displayNameEn || '',
              urlSlug: material.urlSlugEn || '',
              narrative: material.narrativeEn || ''
            }
          },
          gallery: Array.isArray(material.gallery) ? material.gallery.slice() : [],
          videoEmbedUrl: material.videoEmbedUrl || '',
          squareTile: material.squareTile || '',
          heroBanner: material.heroBanner || '',
          materialCategories: categories.slice(),
          tags: tags.slice(),
          tagColor: tagColorHex,
          active: material.active !== false,
          created: material.created || '',
          lastUpdate: material.lastUpdate || material.updatedAgo || '',
          lastEdited: material.lastEdited || material.updatedAgo || '',
          editor: material.editor || ''
        }
        this.isAddingNewCategory = false
        const order = material.sectionOrder
        if (Array.isArray(order) && order.length === 3 &&
            order.includes('general') && order.includes('galerija') && order.includes('youtube')) {
          this.sectionOrder = order.slice()
        } else {
          this.sectionOrder = ['general', 'galerija', 'youtube']
        }
      } else {
        this.form = getEmptyForm()
        this.form.id = this.materialId
        this.isAddingNewCategory = false
        this.fetchMaterial(this.materialId).then((m) => {
          if (m) this.loadMaterial()
        }).catch(() => {})
      }
    },

    onGalleryFilesChange (e) {
      const files = Array.from(e.target.files || [])
      files.forEach((file) => {
        const url = URL.createObjectURL(file)
        this.form.gallery.push({ url, file })
      })
      const input = this.getGalleryFileInput()
      if (input) input.value = ''
    },

    getGalleryFileInput () {
      const ref = this.$refs.galleryFileInput
      return ref ? (Array.isArray(ref) ? ref[0] : ref) : null
    },

    openGalleryFileInput () {
      const input = this.getGalleryFileInput()
      if (input && typeof input.click === 'function') input.click()
    },

    onGalleryDrop (e) {
      this.isDropzoneActive = false
      const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/'))
      files.forEach((file) => {
        const url = URL.createObjectURL(file)
        this.form.gallery.push({ url, file })
      })
    },

    onGalleryDragStart (e, index) {
      this.galleryDraggedIndex = index
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
    },

    onGalleryDragEnd () {
      this.galleryDraggedIndex = null
    },

    onGalleryDragOver (e, index) {
      if (this.galleryDraggedIndex === null) return
      e.dataTransfer.dropEffect = 'move'
    },

    onGalleryDropItem (e, dropIndex) {
      if (e.dataTransfer.files && e.dataTransfer.files.length) {
        const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'))
        files.forEach((file) => {
          const url = URL.createObjectURL(file)
          this.form.gallery.splice(dropIndex, 0, { url, file })
          dropIndex++
        })
        return
      }
      if (this.galleryDraggedIndex === null || this.galleryDraggedIndex === dropIndex) return
      const item = this.form.gallery.splice(this.galleryDraggedIndex, 1)[0]
      this.form.gallery.splice(dropIndex, 0, item)
      this.galleryDraggedIndex = null
    },

    removeGalleryImage (index) {
      const img = this.form.gallery[index]
      if (img && img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url)
      }
      this.form.gallery.splice(index, 1)
    },

    onSectionDragStart (e, index) {
      this.sectionDraggedIndex = index
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
    },
    onSectionDragEnd () {
      this.sectionDraggedIndex = null
    },
    onSectionDragOver (e, index) {
      if (this.sectionDraggedIndex === null) return
      e.dataTransfer.dropEffect = 'move'
    },
    onSectionDrop (e, dropIndex) {
      const fromIndex = this.sectionDraggedIndex
      if (fromIndex === null || fromIndex === dropIndex) return
      const order = this.sectionOrder.slice()
      const [removed] = order.splice(fromIndex, 1)
      order.splice(dropIndex, 0, removed)
      this.sectionOrder = order
      this.sectionDraggedIndex = null
    },

    onSquareTileChange (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (ev) => {
        this.form.squareTile = ev.target.result
      }
      reader.readAsDataURL(file)
      if (this.$refs.squareTileInput) this.$refs.squareTileInput.value = ''
    },

    onHeroBannerChange (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (ev) => {
        this.form.heroBanner = ev.target.result
      }
      reader.readAsDataURL(file)
      if (this.$refs.heroBannerInput) this.$refs.heroBannerInput.value = ''
    },

    onMaterialCategorySelect (value) {
      if (value === ADD_NEW_CATEGORY_VALUE) {
        this.isAddingNewCategory = true
        this.form.materialCategories = []
        this.pendingNewCategory = ''
        this.$nextTick(() => {
          const input = this.$refs.newCategoryInput
          const el = input ? (Array.isArray(input) ? input[0] : input) : null
          if (el && typeof el.focus === 'function') el.focus()
        })
      } else {
        this.isAddingNewCategory = false
        this.form.materialCategories = value ? [value] : []
        this.pendingNewCategory = ''
      }
    },

    commitNewCategory () {
      const trimmed = (this.pendingNewCategory || '').trim()
      if (trimmed) {
        this.form.materialCategories = [trimmed]
        this.pendingNewCategory = ''
        this.isAddingNewCategory = false
      }
    },

    onTagInput (val) {
      const trimmed = (val || '').trim()
      this.form.tags = trimmed ? [trimmed] : []
    },

    onColorInput (e) {
      const hex = e.target && e.target.value
      if (hex) this.form.tagColor = hex
    },

    toggleColorPicker () {
      this.colorPickerOpen = !this.colorPickerOpen
      if (this.colorPickerOpen) {
        this.$nextTick(() => {
          document.addEventListener('click', this.closeColorPickerOnClickOutside)
        })
      } else {
        this.removeColorPickerListener()
      }
    },

    removeColorPickerListener () {
      document.removeEventListener('click', this.closeColorPickerOnClickOutside)
    },

    closeColorPickerOnClickOutside (e) {
      const wrap = this.$refs.colorPickerWrap
      if (wrap && !wrap.contains(e.target)) {
        this.colorPickerOpen = false
        this.removeColorPickerListener()
      }
    },

    selectColor (hex) {
      this.form.tagColor = hex || '#000000'
    },

    openNativeColorPicker () {
      this.$refs.colorInput && this.$refs.colorInput.click()
    },

    onPreviewClick (e) {
      const slug = (this.form.data[this.selectedLang].urlSlug || '').trim()
      if (!slug) {
        e.preventDefault()
        Message.warning('Set the URL slug to preview on the site.')
      }
    },

    async publishChanges () {
      const galleryForPayload = this.form.gallery.map((img) => ({ url: img.file || img.url }))
      const payload = {
        displayName: this.form.data.rs.displayName,
        displayNameEn: this.form.data.en.displayName,
        urlSlug: this.form.data.rs.urlSlug,
        urlSlugEn: this.form.data.en.urlSlug,
        narrative: this.form.data.rs.narrative,
        narrativeEn: this.form.data.en.narrative,
        gallery: galleryForPayload,
        videoEmbedUrl: this.form.videoEmbedUrl,
        squareTile: this.form.squareTile,
        heroBanner: this.form.heroBanner,
        materialCategories: this.form.materialCategories,
        tags: this.form.tags,
        tagColor: this.form.tagColor,
        active: this.form.active,
        sectionOrder: this.sectionOrder.slice()
      }
      try {
        if (this.materialId === 'new') {
          const material = await this.createMaterial(payload)
          Message.success('Material added.')
          this.$router.replace({ name: 'MaterijalEdit', params: { materialId: material.id }, query: {} })
          this.form.id = material.id
        } else {
          await this.updateMaterial({ id: this.form.id, ...payload })
          Message.success('Changes saved.')
          this.loadMaterial()
        }
      } catch {
        Message.error('Failed to save material.')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.materijal-edit {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: inherit;
  font-size: inherit;
}

.materijal-edit__header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
  padding: 2rem 3rem;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
}

.materijal-edit__header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
}

.materijal-edit__header-left {
  flex: 1;
  min-width: 0;
}

.materijal-edit__header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.materijal-edit__lang-toggles {
  display: flex;
  gap: 0.25rem;
}

.materijal-edit__lang-btn {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 9999px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f8fafc; color: #0f172a; }
  &--active {
    background: #1a1a1a;
    color: #fff;
    border-color: #1a1a1a;
  }
}

.materijal-edit__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.materijal-edit__breadcrumb-back {
  display: inline-flex;
  margin-right: 0.5rem;
  color: #64748b;
  text-decoration: none;
  transition: color 0.15s;
  .material-symbols-outlined {
    font-size: 1.25rem;
  }
  &:hover {
    color: #0f172a;
  }
}

.materijal-edit__breadcrumb-link {
  color: #64748b;
  text-decoration: none;
  transition: color 0.15s;
  &:hover {
    color: #0f172a;
  }
}

.materijal-edit__breadcrumb-sep {
  margin: 0 0.375rem;
  color: #94a3b8;
}

.materijal-edit__breadcrumb-current {
  color: #0f172a;
  font-weight: 600;
}

.materijal-edit__title {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin: 0 0 0.5rem 0;
}

.materijal-edit__title-main {
  color: #0f172a;
}

.materijal-edit__title-suffix {
  color: #2563eb;
}

.materijal-edit__meta-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.materijal-edit__meta {
  font-size: 0.8125rem;
  color: #64748b;
}

.materijal-edit__btn-preview {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #fff;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  text-decoration: none;
  &:hover {
    background: #f8fafc;
    color: #0f172a;
    border-color: #e2e8f0;
  }
  .material-symbols-outlined {
    font-size: 0.875rem;
  }
}

.materijal-edit__btn-save {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #1a1a1a !important;
  color: #fff !important;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none !important;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover:not(:disabled) {
    background: #334155 !important;
    color: #fff !important;
    border-color: transparent !important;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .material-symbols-outlined {
    font-size: 0.875rem;
  }
}

.materijal-edit__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 2rem 3rem;
  display: flex;
  gap: 2rem;
  background-color: #f8fafc;
}

.materijal-edit__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.materijal-edit__sidebar {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.materijal-edit__card {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #f1f5f9;
  padding: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.materijal-edit__card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 1rem 0;

  &--uppercase {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #94a3b8;
  }
}

.materijal-edit__card-icon {
  font-size: 1.25rem;
  color: #64748b;
}

.materijal-edit__form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.materijal-edit__form-field {
  flex: 1;
  min-width: 0;
  &--equal { flex: 1; }
}

.materijal-edit__form-group {
  margin-bottom: 1.25rem;
  &:last-child {
    margin-bottom: 0;
  }
}

.materijal-edit__url-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  ::v-deep .el-input {
    flex: 1;
    min-width: 0;
  }
  ::v-deep .el-input__inner {
    border: none;
    border-radius: 0;
    border-left: 1px solid #e2e8f0;
  }
}

.materijal-edit__url-prefix {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #94a3b8;
  background: #f8fafc;
  white-space: nowrap;
}

.materijal-edit__form-input--slug {
  flex: 1;
  min-width: 6rem;
}

.materijal-edit__form-textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  resize: vertical;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #94a3b8; }
}

.materijal-edit__form-select {
  width: 100%;
}

.materijal-edit__add-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  align-items: center;
  &--mt { margin-top: 0.75rem; }
}

.materijal-edit__add-input {
  flex: 1;
  min-width: 0;
}

.materijal-edit__new-category-btn {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
}

.materijal-edit__active-toggle {
  display: flex;
  gap: 1.5rem;
}

.materijal-edit__form-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.5rem;
}

.materijal-edit__form-input {
  width: 100%;
}

.materijal-edit__label-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.materijal-edit__form-input--tag {
  flex: 1;
  min-width: 0;
}

.materijal-edit__color-picker-wrap {
  position: relative;
  display: inline-block;
}

.materijal-edit__color-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.materijal-edit__color-picker-btn {
  width: 2.5rem;
  height: 2rem;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  background-color: #000000;
  &:hover {
    border-color: #cbd5e1;
  }
}

.materijal-edit__color-picker-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  left: auto;
  margin-top: 0.25rem;
  padding: 0.5rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  width: fit-content;
  max-height: 18rem;
  overflow: auto;
}

.materijal-edit__color-swatch-row {
  display: grid;
  grid-template-columns: repeat(11, 1rem);
  gap: 2px;
  margin-bottom: 0.25rem;

  &--spectrum {
    margin-bottom: 0.5rem;
  }
}

.materijal-edit__color-swatch {
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    border-color: #64748b;
    transform: scale(1.1);
  }

  &--none {
    background: #fff !important;
    background-image: linear-gradient(to bottom right, transparent 48%, #ef4444 48%, #ef4444 52%, transparent 52%) !important;
  }
}

.materijal-edit__color-show-more {
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  cursor: pointer;
  &:hover {
    background: #e2e8f0;
    color: #334155;
  }
}

.materijal-edit__section-wrap {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
  &.materijal-edit__section-wrap--dragging {
    opacity: 0.7;
  }
  .materijal-edit__card {
    flex: 1;
    min-width: 0;
  }
}

.materijal-edit__section-drag-handle {
  flex-shrink: 0;
  margin-top: 1.25rem;
  color: #94a3b8;
  pointer-events: none;
  .material-symbols-outlined {
    font-size: 1.25rem;
  }
}

.materijal-edit__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.materijal-edit__gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

.materijal-edit__gallery-slot {
  position: relative;
  aspect-ratio: 1;
  min-width: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #f1f5f9;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  &:active {
    cursor: grabbing;
  }
  &.materijal-edit__gallery-slot--dragging {
    opacity: 0.6;
  }

  &--add {
    cursor: pointer;
    border-style: dashed;
    .material-symbols-outlined {
      font-size: 2rem;
      color: #94a3b8;
    }
    &:hover {
      border-color: #cbd5e1;
      .material-symbols-outlined {
        color: #64748b;
      }
    }
  }
}

.materijal-edit__gallery-slot-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.materijal-edit__gallery-slot-remove {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  .material-symbols-outlined {
    font-size: 0.75rem;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
}

.materijal-edit__gallery-drag-handle {
  position: absolute;
  bottom: 0.25rem;
  left: 0.25rem;
  color: rgba(255, 255, 255, 0.8);
  .material-symbols-outlined {
    font-size: 1rem;
  }
}

.materijal-edit__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px dashed #e2e8f0;
  border-radius: 0.75rem;
  padding: 2rem;
  transition: border-color 0.15s, background 0.15s;
  cursor: pointer;
  &.materijal-edit__dropzone--active {
    border-color: #94a3b8;
    background: #f8fafc;
  }
}

.materijal-edit__dropzone-icon {
  font-size: 3rem;
  color: #94a3b8;
}

.materijal-edit__dropzone-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
  margin: 0;
}

.materijal-edit__dropzone-hint {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin: 0;
}

.materijal-edit__dropzone-btn {
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
}

.materijal-edit__input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
  .el-input {
    width: 100%;
  }
  ::v-deep .el-input__inner {
    padding-left: 2.5rem;
  }
}

.materijal-edit__input-icon {
  position: absolute;
  left: 0.75rem;
  font-size: 1.125rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}

.materijal-edit__video-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  aspect-ratio: 16 / 9;
  background: #f1f5f9;
  border-radius: 0.75rem;
  margin-top: 1rem;
}

.materijal-edit__video-placeholder-icon {
  font-size: 3rem;
  color: #94a3b8;
}

.materijal-edit__video-placeholder-text {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #94a3b8;
}

.materijal-edit__radio-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.materijal-edit__radio-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
}

.materijal-edit__radio-dot {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}

.materijal-edit__radio-input:checked + .materijal-edit__radio-dot {
  border-color: #2563eb;
  background: #fff;
  box-shadow: inset 0 0 0 3px #fff, inset 0 0 0 6px #2563eb;
}

.materijal-edit__radio-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
}

.materijal-edit__video-wrap {
  margin-top: 1rem;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #0f172a;
  aspect-ratio: 16 / 9;
}

.materijal-edit__video-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.materijal-edit__covers {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.materijal-edit__cover-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.materijal-edit__cover-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
}

.materijal-edit__cover-box {
  aspect-ratio: 1;
  border-radius: 0.5rem;
  border: 1px dashed #e2e8f0;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  transition: border-color 0.15s;
  &:hover {
    border-color: #cbd5e1;
  }
  &.materijal-edit__cover-box--square {
    aspect-ratio: 1;
  }
  &.materijal-edit__cover-box--hero {
    aspect-ratio: 21 / 9;
  }
}

.materijal-edit__cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.materijal-edit__cover-placeholder {
  color: #94a3b8;
  .material-symbols-outlined {
    font-size: 2rem;
  }
}

</style>
